import React, {ForwardedRef, forwardRef, ReactElement, useEffect, useMemo} from "react";
import {UNDEFINED_BRAND_STYLES_RESULT} from "data/access/deliverables/useBrandStyles";
import WebFont from "webfontloader";
import {BrandStylesResult} from "data/models/deliverables/brand-styles";
import Loading from "components/Loading";
import {CopyToClipboardButton} from "components/CopyToClipboardButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCopy} from "@fortawesome/free-solid-svg-icons";
import {isMobileBreakpoint} from "hooks/tailwind/breakpoint";

type BrandStylesThumbProps = {
  businessName: string | null;
  brandStyles: BrandStylesResult | null;
  brandVoice: string | null;
  brandVoiceLoading: boolean;
  alternativeText: string;
}


const BrandStylesThumb = forwardRef<HTMLDivElement, BrandStylesThumbProps>((
    {
      businessName,
      brandStyles,
      brandVoice,
      brandVoiceLoading,
      alternativeText,
    }: BrandStylesThumbProps,
    ref: ForwardedRef<HTMLDivElement>
) => {

  const isSmallBreakpoint = isMobileBreakpoint();

  const {
    brandColors,
    brandFont,
    subtitleFont,
    headingFont,
    subheadingFont,
    sectionHeaderFont,
    bodyFont,
    quoteFont,
    captionFont,
  } = useMemo(() => brandStyles ?? UNDEFINED_BRAND_STYLES_RESULT, [brandStyles]);

  const hasBrandStyles = brandStyles !== null;

  useEffect(() => {

    WebFont.load({
      google: {
        families: [...new Set([
          brandFont.family,
          subtitleFont.family,
          headingFont.family,
          subheadingFont.family,
          sectionHeaderFont.family,
          bodyFont.family,
          quoteFont.family,
          captionFont.family,
        ])],
      },
    });
  }, [
    brandFont,
    subtitleFont,
    headingFont,
    subheadingFont,
    sectionHeaderFont,
    bodyFont,
    quoteFont,
    captionFont,
  ]);

  const noBrandStylesFontColor = "#808285";

  const sectionHeaderStyle = {
    fontFamily: sectionHeaderFont.name,
    fontWeight: sectionHeaderFont.weight ?? 400,
    fontStyle: sectionHeaderFont.italic ? "italic" : "normal",
    fontSize: isSmallBreakpoint ? "0.6rem" : "0.8rem",
    color: hasBrandStyles ? undefined : noBrandStylesFontColor,
  };

  const captionStyle = {
    fontFamily: captionFont.name,
    fontWeight: captionFont.weight ?? 400,
    fontStyle: captionFont.italic ? "italic" : "normal",
    fontSize: isSmallBreakpoint ? "0.4rem" : "0.6rem",
    color: hasBrandStyles ? undefined : noBrandStylesFontColor,
  };

  const colorCodesStyle = {
    ...captionStyle,
    fontWeight: 400,
  };

  const bodyStyle = {
    fontFamily: bodyFont.name,
    fontWeight: bodyFont.weight ?? 400,
    fontStyle: bodyFont.italic ? "italic" : "normal",
    fontSize: isSmallBreakpoint ? "0.4rem" : "0.8rem",
    color: hasBrandStyles ? undefined : noBrandStylesFontColor,
  };

  const CopyButton = ({handleCopy}: {
    handleCopy: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  }): ReactElement => <a
      className={"cursor-pointer"}
      color={"blue"}
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        handleCopy(event);
      }}
  >
    <FontAwesomeIcon icon={faCopy} className={"ml-4 text-bc-blue"} size={"sm"}/>
  </a>;

  return (<div ref={ref} className={"flex flex-col p-8 text-left border-3 rounded-xl border-neutral-100"}>
        <div
            className={"font-bold text-base text-center text-neutral-500"}
            style={{
              fontFamily: brandFont.name,
              color: brandColors.primary.color,
            }}
        >{businessName ?? ''} Brand Styles
        </div>
        <div
            className={"mt-2"}
            style={sectionHeaderStyle}
        >
          Fonts
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full"}
              style={{
                fontFamily: brandFont.name,
                fontWeight: brandFont.weight ?? 400,
                fontStyle: brandFont.italic ? "italic" : "normal",
                fontSize: isSmallBreakpoint ? "1.8rem" : "2.5rem",
                color: hasBrandStyles ? brandColors.primary.color : noBrandStylesFontColor,
              }}>{hasBrandStyles ? `Title: ${brandFont.name} 72pt bold` : "Title Type Style: font, size"}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full"}
              style={{
                fontFamily: subtitleFont.name,
                fontWeight: subtitleFont.weight ?? 400,
                fontStyle: subtitleFont.italic ? "italic" : "normal",
                fontSize: isSmallBreakpoint ? "1.4rem" : "2.0rem",
                color: hasBrandStyles ? brandColors.secondary.color : noBrandStylesFontColor,
              }}>{hasBrandStyles ? `Subtitle: ${subtitleFont.name} 64pt bold` : "Subtitle Type Style: font, size"}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full"}
              style={{
                fontFamily: headingFont.name,
                fontWeight: headingFont.weight ?? 400,
                fontStyle: headingFont.italic ? "italic" : "normal",
                fontSize: isSmallBreakpoint ? "1.0rem" : "1.8rem",
                color: hasBrandStyles ? brandColors.primary.color : noBrandStylesFontColor,
              }}>{hasBrandStyles ? `Heading: ${headingFont.name} 56pt bold` : "Heading Type Style: font, size"}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full text-sm"}
              style={{
                fontFamily: subheadingFont.name,
                fontWeight: subheadingFont.weight ?? 400,
                fontSize: isSmallBreakpoint ? "0.8rem" : "1.5rem",
                fontStyle: subheadingFont.italic ? "italic" : "normal",
                color: hasBrandStyles ? brandColors.secondary.color : noBrandStylesFontColor,
              }}>{hasBrandStyles ? `Subheading: ${subheadingFont.name} 48pt bold` : "Subheading Type Style: font, size"}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full"}
              style={sectionHeaderStyle}
          >{hasBrandStyles ? `Section Header: ${sectionHeaderFont.name} 32pt bold` : "Section Header Type Style: font, size"}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full leading-1 md:leading-4 display-inline-block"}
              style={bodyStyle}>{hasBrandStyles ? `Body: ${bodyFont.name} 16pt.` : "Body Type Style: font, size. This is used for longer paragraphs of text. This style sheet includes all eight type styles that Canva uses in its brand kit. The style include the font, size and weight. All fonts are Google fonts which are free to use. They may be immediately available in Canva or, you can upload the font into your Canva account."}</span>
        </div>
        <div className={"mt-2"}>
          <span
              className={"w-full text-xs"}
              style={{
                fontFamily: quoteFont.name,
                fontWeight: quoteFont.weight ?? 400,
                fontStyle: quoteFont.italic ? "italic" : "normal",
                fontSize: isSmallBreakpoint ? "0.7rem" : "1.5rem",
                color: hasBrandStyles ? undefined : noBrandStylesFontColor,
              }}>{hasBrandStyles ? `Quote: ${quoteFont.name} 40pt` : "Quote Type Style: font, size"}</span>
        </div>
        <div>
          <span
              className={"w-full"}
              style={captionStyle}
          >{hasBrandStyles ? `Caption: ${captionFont.name} 16pt bold` : "Caption Type Style: font, size"}</span>
        </div>
        <div
            className={"mt-2 md:mt-6"}
            style={sectionHeaderStyle}
        >
          Colors
        </div>
        <div className={"mt-1 md:mt-2 flex flex-row"}>
          <div className={"w-1/2 mr-1 flex flex-col"}>
            <div style={captionStyle}>PRIMARY</div>
            <div
                className={"w-full h-24"}
                style={{backgroundColor: brandColors.primary.color}}
            ></div>
            <div style={colorCodesStyle}>{brandColors.primary.color.toUpperCase()}</div>
          </div>
          <div className={"w-1/4 mx-1 flex flex-col ml"}>
            <div style={captionStyle}>SECONDARY</div>
            <div
                className={"w-full h-24"}
                style={{backgroundColor: brandColors.secondary.color}}
            ></div>
            <div style={colorCodesStyle}>{brandColors.secondary.color.toUpperCase()}</div>
          </div>
          <div className={"w-1/4 ml-1 flex flex-col"}>
            <div style={captionStyle}>BACKGROUND</div>
            <div
                className={"w-full h-10"}
                style={{backgroundColor: brandColors.background.color}}
            ></div>
            <div style={colorCodesStyle}>{brandColors.background.color.toUpperCase()}</div>
            <div className={"grow flex flex-col justify-end"}>
              <div style={captionStyle}>ACCENT</div>
              <div
                  className={"w-full h-4"}
                  style={{backgroundColor: brandColors.accent.color}}
              ></div>
              <div style={colorCodesStyle}>{brandColors.accent.color.toUpperCase()}</div>
            </div>
          </div>
        </div>
        <div className={"flex flex-row mt-8"}>
          <div
              className={"my-auto"}
              style={sectionHeaderStyle}
          >
            Brand Voice
          </div>
          {brandVoice !== null && !brandVoiceLoading && <div className={"my-auto"}>
              <CopyToClipboardButton text={brandVoice} CopyButton={CopyButton}/>
          </div>}
        </div>
        {!brandVoiceLoading && <div
            className={"mt-2 overflow-scroll"}
            style={bodyStyle}
        >
          {brandVoice ?? <span className={"text-neutral-500 text-xs md:text-base leading-4"}>{alternativeText}</span>}
        </div>}

        {brandVoiceLoading &&
            <div className={"flex flex-col h-full justify-center"}><Loading loadingText={"Generating brand voice..."}/>
            </div>}


        <div className={"mt-8 flex flex-row justify-between"}>
          <div className={"w-1/6"}>
            <img src={"/brandingcompass-r-logo.png"} alt={"Branding Compass"}/>
          </div>
          <div className={"text-xs align-bottom mt-auto"}>
            <a href={"https://www.brandingcompass.com"} target={"_blank"} rel={"noreferrer"}>BrandingCompass.com</a>
            <span className={"ml-2 text-gray-500"}>&copy;{new Date().getFullYear()} Visible Logic, Inc.</span>
          </div>
        </div>
      </div>
  )
});

export default BrandStylesThumb;
